import type {
  Category,
  CategoryItem,
  Event,
  EventTranslation,
  News,
  Outcome,
  OutcomeTranslation,
  PopularTag,
  RelatedEvent,
  RelatedNews,
  Resolution,
  ResolutionTranslation,
} from '@/lib/api/event/models';
import type {
  CategoryDto,
  CategoryItemDto,
  EventDto,
  EventTranslationDto,
  NewsDto,
  OutcomeDto,
  OutcomeTranslationDto,
  PopularTagDto,
  RelatedEventDto,
  RelatedNewsDto,
  ResolutionDto,
  ResolutionTranslationDto,
} from '@/lib/api/event/types';

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
}

export function mapCategory(dto: CategoryDto): Category {
  return {
    id: dto.i,
    name: dto.n,
    slug: dto.s,
  };
}

export function mapCategoryItem(dto: CategoryItemDto): CategoryItem {
  return { id: dto.i, name: dto.n, slug: dto.s, parentId: dto.pi };
}

export function mapPopularTag(dto: PopularTagDto): PopularTag {
  return { name: dto.n, rank: dto.r, volume: toNumberOrNull(dto.v), eventsCount: dto.c ?? 0 };
}

function mapEventTranslation(dto: EventTranslationDto): EventTranslation {
  return {
    id: dto.i,
    locale: dto.l,
    title: dto.ti,
    slug: dto.s,
    description: dto.d,
    rule: dto.r,
  };
}

function mapOutcomeTranslation(dto: OutcomeTranslationDto): OutcomeTranslation {
  return {
    id: dto.i,
    locale: dto.l,
    question: dto.q,
    slug: dto.s,
    description: dto.d,
  };
}

function mapResolutionTranslation(dto: ResolutionTranslationDto): ResolutionTranslation {
  return {
    id: dto.i,
    locale: dto.l,
    name: dto.n,
  };
}

function mapResolution(dto: ResolutionDto): Resolution {
  return {
    id: dto.i,
    name: dto.n,
    price: dto.p !== null ? toNumberOrNull(dto.p) : null,
    outcomeId: dto.o,
    isPositive: dto.ip,
    isWin: dto.iw,
    translations: dto.tr?.map(mapResolutionTranslation),
  };
}

export function mapOutcome(dto: OutcomeDto): Outcome {
  // Extract resolution names and prices from the new structure
  const resolutions = (dto.r ?? []).map((res) => res.n);
  const resolutionPrices = (dto.r ?? [])
    .map((res) => {
      if (res.p == null) return null;
      const num = Number(res.p);
      return Number.isNaN(num) ? null : String(res.p);
    })
    .filter((p): p is string => p !== null);

  // Map full resolution objects
  const r = (dto.r ?? []).map(mapResolution);

  return {
    id: dto.i,
    question: dto.q,
    slug: dto.s,
    liquidity: toNumberOrNull(dto.l),
    icon: dto.c ?? undefined,
    image: dto.im ?? undefined,
    description: dto.d,
    resolutions,
    resolutionPrices,
    r, // Full resolution objects array
    translations: dto.tr?.map(mapOutcomeTranslation),
    volume: toNumberOrNull(dto.v),
    lastTradePrice: toNumberOrNull(dto.a),
    bestAsk: toNumberOrNull(dto.b),
    bestBid: toNumberOrNull(dto.t),
    status: dto.st as Outcome['status'],
    isDraw: dto.dr,
  };
}

export function mapNews(dto: NewsDto): News {
  return {
    id: dto.i,
    title: dto.t,
    description: dto.d,
    image: dto.img,
    date: dto.dt,
    url: dto.url,
  };
}

export function mapRelatedNews(dto: RelatedNewsDto): RelatedNews {
  return {
    id: dto.i,
    title: dto.t,
    slug: dto.s,
    publishedAt: dto.pb,
    category: dto.ct ? { id: dto.ct.i, name: dto.ct.n, slug: dto.ct.s } : null,
    tags: dto.tg ? dto.tg.split(',').map((t) => t.trim()) : [],
    imageLink: dto.im ?? null,
    commentCount: dto.cc ?? 0,
  };
}

export function mapRelatedEvent(dto: RelatedEventDto): RelatedEvent {
  return {
    id: dto.i,
    title: dto.ti,
    slug: dto.s,
    image: dto.m,
    createdAt: dto.ca,
    categories: (dto.ct ?? []).map((c) => ({ id: c.i, name: c.n, slug: c.s })),
    tags: (dto.tg ?? []).map((t) => ({ id: t.i, name: t.n })),
    volume: toNumberOrNull(dto.u),
    outcomeName: dto.on,
    resolutionName: dto.rn,
    chance: toNumberOrNull(dto.ch),
    endDate: dto.e,
  };
}

export function mapEvent(dto: EventDto): Event {
  return {
    id: dto.i,
    slug: dto.s,
    title: dto.ti ?? '',
    description: dto.d,
    rule: dto.r,
    startDate: dto.a,
    endDate: dto.e,
    endTradeDate: dto.et,
    resolvedAt: dto.rd ?? undefined,
    ambassadorSocialLink: dto.asl,
    image: dto.m,
    icon: dto.c,
    active: dto.v,
    isSportMarket: dto.sm,
    isCryptoMarket: dto.cm,
    volume: toNumberOrNull(dto.u),
    createdAt: dto.ca,
    updatedAt: dto.ua,
    markets: (dto.ma ?? []).map(mapOutcome),
    categories: (dto.ct ?? []).map(mapCategory),
    translations: dto.tr?.map(mapEventTranslation),
    translatedLocales: dto.lc,
    tags: (dto.tg ?? []).map((t) => ({ id: t.i, name: t.n })),
    relatedNewsCount: dto.nc ?? 0,
    participantCount: dto.pc ?? null,
    isFavorite: dto.fv ?? false,
    isFeatured: dto.ft ?? false,
  };
}
